import { useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { Checkbox } from "@jugl-web/ui-components/cross-platform";
import { InvoiceModel } from "@jugl-web/rest-api";
import { useInvoiceTypeToLabelDictionary } from "@jugl-web/rest-api/invoices/hooks/useInvoiceTypeToLabelDictionary";
import { TableGridColumn } from "@jugl-web/ui-components/cross-platform/content/types";
import {
  TableCell,
  TableDateCell,
  TablePriceCell,
} from "@jugl-web/ui-kit/data-display/Table";
import { InvoicesStatusBadge } from "../components/InvoicesStatusBadge";
import { InvoicesItemSettings } from "../components/InvoicesItemSettings";
import { CustomerCell } from "../components/Cells/CustomerCell";
import { CheckboxCell } from "../components/Cells/CheckboxCell/CheckboxCell";
import { DueDateCell } from "../components/Cells/DueDateCell/DueDateCell";
import { useInvoicesTableContext } from "../context/useInvoicesTableContext";
import { ComposedCell } from "../components/Cells/ComposedCell";

export const useInvoiceTableColumns = () => {
  const { t } = useTranslations();
  const { items, variant, selected } = useInvoicesTableContext();
  const [previewInvoice, setPreviewInvoice] = useState<InvoiceModel>();
  const dict = useInvoiceTypeToLabelDictionary();

  const isCustomerVariant = variant === "customer";

  const columns: TableGridColumn<InvoiceModel>[] = [
    {
      id: "checkbox",
      title: (
        <Checkbox
          size="sm"
          isChecked={Boolean(items?.length) && selected.allSelected}
          onChange={() => selected.toggleAll()}
          isDisabled={!items?.length}
        />
      ),
      width: 50,
      headerClassName: "rounded-tl-xl",
      content: (item: InvoiceModel) => (
        <div className="flex w-full items-center justify-center">
          <CheckboxCell id={item.id} />
        </div>
      ),
      isHidden: isCustomerVariant,
    },
    {
      id: "composed",
      title: t({
        defaultMessage: "Document № & Type",
        id: "invoices.composed-number-cell",
      }),
      width: 160,
      headerClassName: "rounded-tl-xl",
      content: (item: InvoiceModel) => (
        <ComposedCell
          details={[
            { id: "invoice-number", value: item.no },
            { id: "type", value: dict[item.type] },
            {
              id: "payment-status",
              value: item.status,
            },
          ]}
        />
      ),
      onCellClick: (item: InvoiceModel) => setPreviewInvoice(item),
      className: "cursor-pointer",
      isHidden: !isCustomerVariant,
    },
    {
      id: "invoice-number",
      title: t({
        id: "invoices.invoice-number",
        defaultMessage: "Invoice Number",
      }),
      content: (item: InvoiceModel) => (
        <TableCell>
          <div className="font-medium">{item.no}</div>
        </TableCell>
      ),
      onCellClick: (item: InvoiceModel) => {
        setPreviewInvoice(item);
      },
      className: "cursor-pointer",
      isHidden: isCustomerVariant,
    },
    {
      id: "reference-number",
      title: t({
        id: "invoices.reference-number",
        defaultMessage: "Reference Number",
      }),
      content: (item: InvoiceModel) => (
        <TableCell>{item.ref_num || "-"}</TableCell>
      ),
      onCellClick: (item: InvoiceModel) => {
        setPreviewInvoice(item);
      },
      className: "cursor-pointer",
      isHidden: isCustomerVariant,
    },
    {
      id: "type",
      title: t({
        id: "invoices.type",
        defaultMessage: "Type",
      }),
      width: 150,
      content: (item: InvoiceModel) => <TableCell>{dict[item.type]}</TableCell>,
      onCellClick: (item: InvoiceModel) => {
        setPreviewInvoice(item);
      },
      className: "cursor-pointer",
      isHidden: isCustomerVariant,
    },
    {
      id: "issue-date",
      title: t({
        defaultMessage: "Issue date",
        id: "invoices.issue-date",
      }),
      content: (item: InvoiceModel) => (
        <TableDateCell date={new Date(item.date)} />
      ),
      onCellClick: (item: InvoiceModel) => setPreviewInvoice(item),
      className: "cursor-pointer",
      width: 120,
      isHidden: isCustomerVariant,
    },
    {
      id: "due-date",
      title: t({
        defaultMessage: "Due date",
        id: "invoices.due-date",
      }),
      content: (item: InvoiceModel) => (
        <DueDateCell
          due_on={item.due_on}
          useConciseFormat={isCustomerVariant}
        />
      ),
      onCellClick: (item: InvoiceModel) => setPreviewInvoice(item),
      className: "cursor-pointer",
      width: 145,
    },
    {
      id: "customer",
      title: t({
        defaultMessage: "Customer",
        id: "invoices.customer",
      }),
      grow: true,
      content: (item: InvoiceModel) =>
        item.customer_id ? <CustomerCell {...item} /> : <>-</>,
      onCellClick: (item: InvoiceModel) => setPreviewInvoice(item),
      className: "cursor-pointer min-w-content",
      isHidden: isCustomerVariant,
    },
    {
      id: "payment-status",
      title: t({
        defaultMessage: "Payment status",
        id: "invoices.payment-status",
      }),
      content: (item: InvoiceModel) => (
        <InvoicesStatusBadge status={item.status} />
      ),
      onCellClick: (item: InvoiceModel) => setPreviewInvoice(item),
      className: "cursor-pointer h-16",
      isHidden: isCustomerVariant,
    },
    {
      id: "total-value",
      title: t({
        defaultMessage: "Total value",
        id: "invoices.total-value",
      }),
      grow: true,
      width: 140,
      content: (item: InvoiceModel) => (
        <TablePriceCell
          price={item.grand_total ? item.grand_total * 100 : 0}
          currency={item.currency}
        />
      ),
      onCellClick: (item: InvoiceModel) => setPreviewInvoice(item),
      className: "cursor-pointer",
    },
    {
      id: "share-link-directly",
      title: t({
        defaultMessage: "Share link directly",
        id: "invoices.share-link-directly",
      }),
      grow: true,
      content: (item: InvoiceModel) => (
        <TableCell clipboardContent={item.short_url}>
          {item.short_url}
        </TableCell>
      ),
      isHidden: isCustomerVariant,
    },
    {
      id: "settings",
      title: t({
        defaultMessage: "Settings",
        id: "invoices.settings",
      }),
      headerClassName: "rounded-tr-xl border-r-0",
      content: (item: InvoiceModel) => <InvoicesItemSettings item={item} />,
    },
  ];

  return {
    columns,
    previewInvoice,
    setPreviewInvoice,
  };
};
